import { useContext, useMemo } from "react";
import { OnlinePaymentsConfigContext } from "./OnlinePaymentsConfigContext";
import type { OnlinePaymentsConfigContextType } from "./types";

export const useOnlinePaymentsConfig = (): OnlinePaymentsConfigContextType => {
  const context = useContext(OnlinePaymentsConfigContext);
  if (context === undefined) {
    throw new Error(
      "useOnlinePaymentsConfig deve ser usado dentro de um OnlinePaymentsConfigProvider",
    );
  }
  return context;
};

export const useProviderFormContext = () => {
  const context = useOnlinePaymentsConfig();

  return useMemo(
    () => ({
      register: context.register,
      watch: context.watch,
      setValue: context.setValue,
      control: context.control,
      formErrors: context.formErrors,
      trigger: context.trigger,
      getValues: context.getValues,
      clearErrors: context.clearErrors,
      handleSubmit: context.handleSubmit,
      reset: context.reset,
      selectedProvider: context.selectedProvider,
      providers: context.providers,
      priorityOptions: context.priorityOptions,
      mcc: context.mcc,
      internalReadOnly: context.internalReadOnly,
      mode: context.mode,
    }),
    [
      context.register,
      context.watch,
      context.setValue,
      context.control,
      context.formErrors,
      context.trigger,
      context.getValues,
      context.clearErrors,
      context.handleSubmit,
      context.reset,
      context.selectedProvider,
      context.providers,
      context.priorityOptions,
      context.mcc,
      context.internalReadOnly,
      context.mode,
    ],
  );
};

export const useTabNavigationContext = () => {
  const context = useOnlinePaymentsConfig();

  return useMemo(
    () => ({
      currentTab: context.currentTab,
      previousTab: context.previousTab,
      isAnimating: context.isAnimating,
      tabErrors: context.tabErrors,
      handleChangeTab: context.handleChangeTab,
      setCurrentTab: context.setCurrentTab,
      getAnimationDirection: context.getAnimationDirection,
      showCredentialsTab: context.showCredentialsTab,
      showBrandsTab: context.showBrandsTab,
    }),
    [
      context.currentTab,
      context.previousTab,
      context.isAnimating,
      context.tabErrors,
      context.handleChangeTab,
      context.setCurrentTab,
      context.getAnimationDirection,
      context.showCredentialsTab,
      context.showBrandsTab,
    ],
  );
};

export const useValidation = () => {
  const context = useOnlinePaymentsConfig();

  return useMemo(
    () => ({
      errors: context.errors,
      setTabErrors: context.setTabErrors,
      hasAnyErrors: context.hasAnyErrors,
      clearAllErrors: context.clearAllErrors,
      isValidationActive: context.isValidationActive,
      activateValidation: context.activateValidation,
      setFormErrors: context.setFormErrors,
    }),
    [
      context.errors,
      context.setTabErrors,
      context.hasAnyErrors,
      context.clearAllErrors,
      context.isValidationActive,
      context.activateValidation,
      context.setFormErrors,
    ],
  );
};
